import React, { useEffect, useState } from 'react';
import { Layout } from "./components/dashboard/layout";
import { Box, Typography, Avatar, Alert, TextField, Autocomplete, Button } from '@mui/material';
import useScanDetection from 'use-scan-detection';

const CheckOut = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alert, setAlert] = useState(null);
  const [indianTime, setIndianTime] = useState("");
  const [loading, setLoading] = useState("");
  const [actionType, setActionType] = useState(null);
  const [checkInTime, setCheckInTime] = useState(null);

  useScanDetection({
    onComplete: (input) => handleUsbInput(input),
    minLength: 3,
    maxLength: 20,
    averageSpeed: 30,
  });

  const updateTime = () => {
    const now = new Date();
    const formatted = now.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    setIndianTime(formatted);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch("https://raptornet.aischennai.org/screening-manager/gate-api/getAllUsers.php");
      const data = await response.json();
      const formattedUsers = data.users.map(user => ({
        userId: user.enroll_no,
        name: user.full_name,
        rfid: user.rfid_tag,
        role: user.user_type,
        grade_level: user.grade,
        person_pk: user.person_pk,
        image_status: user.image_status
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleUsbInput = async (input) => {
  setLoading("Loading...");
  setAlert(null);

  //let normalizedInput = input.replaceAll('Shift', '').replaceAll('ENTER', '').toUpperCase();

  try {
    const response = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/markAttendanceRfid.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rfid: input, action: "checkin" })
    });

    const result = await response.json();

    if (result.success && result.user) {
      const user = result.user;

      const matchedUser = {
        userId: user.aisc_code,
        name: `${user.first_name} ${user.last_name}`,
        rfid: user.rfid,
        role: user.fac_type || 'N/A',
        grade_level: user.grade || '',
        person_pk: user.person_pk,
        image_status: true // assume true for now, or adjust if API returns it
      };

      setSelectedUser(matchedUser);
      setAlert({ severity: 'success', message: result.message || 'Check-In successful' });
    } else {
      setAlert({ severity: 'error', message: result.message || 'RFID not recognized.' });
      setSelectedUser(null);
    }
  } catch (error) {
    console.error('RFID API error:', error);
    setAlert({ severity: 'error', message: 'Error contacting RFID API.' });
    setSelectedUser(null);
  }

  setLoading("");
};


  const handleCheckIn = async (user) => {
    setLoading("Processing Check-In...");
    setAlert(null);
    updateTime();

    try {
      const response = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/markAttendanceManual.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ps_code: user.userId, action: 'checkin' })
      });

      const result = await response.json();
      if (result.success) {
        setCheckInTime(indianTime);
        setAlert({ severity: 'success', message: 'Check-In successful' });
      } else {
        setAlert({ severity: 'error', message: result.message || 'Failed to check in.' });
      }
    } catch (error) {
      setAlert({ severity: 'error', message: 'Error calling check-in API' });
    } finally {
      setLoading("");
    }
  };

  const handleCheckOut = async () => {
    setLoading("Processing Check-Out...");
    setAlert(null);
    setActionType('out');

    console.log('Simulating check-out log for:', selectedUser.userId);
    setAlert({ severity: 'success', message: 'Check-Out successful' });
    setLoading("");
  };

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 3000); // 2 seconds

      return () => clearTimeout(timer); // Clean up if alert changes or component unmounts
    }
  }, [alert]);


  useEffect(() => {
    updateTime();
    fetchUsers();
  }, []);

  return (
    <Layout>
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Manual User Check-In</Typography>
        {alert && <Alert severity={alert.severity} sx={{ mb: 2 }}>{alert.message}</Alert>}

        <Autocomplete
          options={users}
          getOptionLabel={(option) => `${option.userId} - ${option.name}`}
          onChange={(event, newValue) => {
            setSelectedUser(newValue);
            setActionType(null);
            setCheckInTime(null);
          }}
          renderOption={(props, option) => (
            <li {...props} data-rfid={option.rfid}>{`${option.userId} - ${option.name}`}</li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Search User" variant="outlined" fullWidth />
          )}
        />

        {selectedUser && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 3, borderRadius: '10px', backgroundColor: actionType === 'in' ? '#d4edda' : actionType === 'out' ? '#f8d7da' : 'transparent', mt: 3, border: actionType === 'in' ? '2px solid #155724' : actionType === 'out' ? '2px solid #721c24' : 'none' }}>
              <Avatar src={`https://raptornet.aischennai.org/screening-manager/api/images/${selectedUser.userId}.jpg`} alt={selectedUser.name} sx={{ width: 150, height: 150 }} />
              <Box>
                <Typography variant="h4">{selectedUser.name}</Typography>
                <Typography variant="h4">{selectedUser.userId}</Typography>
                {selectedUser.person_type === '3' && (
                  <Typography variant="h6">Grade Level: {selectedUser.grade}</Typography>
                )}
                {selectedUser.person_type === '2' && (
                  <Typography variant="h6">Parent</Typography>
                )}
                {selectedUser.person_type === '4' && (
                  <Typography variant="h6">Staff/Faculty/Vendor</Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={() => handleCheckIn(selectedUser)} disabled={loading !== ""}>Check-In</Button>
              <Button variant="contained" color="secondary" onClick={handleCheckOut} disabled={loading !== ""}>Check-Out</Button>
            </Box>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default CheckOut;